import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import {
  ArrowLeft, Zap, Copy, Share2, Users, CheckCircle,
  Clock, Loader2, AlertCircle, UserCheck, XCircle, Banknote, Building2
} from 'lucide-react';
import { toast } from 'sonner';

import { API_URL as BACKEND_URL } from '../config/api';
import PayoLogo from './PayoLogo';

const QuickSplitHost = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [payments, setPayments] = useState([]);
  const [pageState, setPageState] = useState('loading');
  const [errorMsg, setErrorMsg] = useState('');
  const [isClosing, setIsClosing] = useState(false);
  const [confirmExtSlot, setConfirmExtSlot] = useState(null);
  const [isMarkingExt, setIsMarkingExt] = useState(false);
  const [extPayerName, setExtPayerName] = useState('');
  const pollRef = useRef(null);

  const payUrl = `${window.location.origin}/quick-split/${sessionId}`;

  useEffect(() => {
    fetchSession();
    return () => clearInterval(pollRef.current);
  }, [sessionId]);

  const fetchSession = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/quick-split/sessions/${sessionId}`, {
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setSession(data);
        setPayments(data.payments || []);
        setPageState('ready');
        if (data.status === 'active') startPolling();
      } else if (res.status === 401) {
        toast.error('Please log in first');
        navigate(-1);
      } else {
        setErrorMsg('Transaction not found');
        setPageState('error');
      }
    } catch (err) { console.error("QS host fetch error:", err);
      setErrorMsg('Connection error');
      setPageState('error');
    }
  };

  const startPolling = () => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/quick-split/sessions/${sessionId}/status`, {
          credentials: 'include',
        });
        if (res.ok) {
          const data = await res.json();
          setSession(prev => prev ? {
            ...prev,
            paid_count: data.paid_count,
            collected_amount_cents: data.collected_amount_cents,
            status: data.status,
            link_active: data.link_active,
            link_expires_at: data.link_expires_at,
          } : prev);
          setPayments(data.payments || []);
          if (data.status === 'completed' || data.status === 'closed' || data.status === 'expired') {
            clearInterval(pollRef.current);
          }
        } else if (res.status === 429) {
          // Rate limited — back off by stopping polling
          clearInterval(pollRef.current);
        }
      } catch (err) { console.error("QS host poll error:", err); }
    }, 10000);
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(payUrl);
      toast.success('Link copied!');
    } catch (err) { console.error("QS host copy error:", err);
      const el = document.createElement('textarea');
      el.value = payUrl;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      toast.success('Link copied!');
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: session?.description || 'Quick Split',
          text: `Pay your share: $${session ? (session.per_person_amount_cents / 100).toFixed(2) : ''}`,
          url: payUrl,
        });
      } catch (err) { /* user cancelled share */ }
    } else {
      handleCopyLink();
    }
  };

  const handleClose = async () => {
    setIsClosing(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/quick-split/sessions/${sessionId}/close`, {
        method: 'POST', credentials: 'include',
      });
      if (res.ok) {
        setSession(prev => prev ? { ...prev, status: 'closed', link_active: false } : prev);
        clearInterval(pollRef.current);
        toast.success('Split closed');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to close');
      }
    } catch {
      toast.error('Connection error');
    } finally {
      setIsClosing(false);
    }
  };

  const [isExtending, setIsExtending] = useState(false);
  const [extendHours, setExtendHours] = useState(null);

  const handleExtend = async (hours) => {
    setIsExtending(true);
    setExtendHours(hours);
    try {
      const res = await fetch(`${BACKEND_URL}/api/quick-split/sessions/${sessionId}/extend`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hours }),
      });
      if (res.ok) {
        const data = await res.json();
        const n = data.notified_count || 0;
        toast.success(
          n > 0
            ? `Extended by ${hours}h • ${n} unpaid participant${n === 1 ? '' : 's'} notified`
            : `Extended by ${hours}h`,
        );
        // Refetch so the page repaints with the new active state, link, and timer.
        fetchSession();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(typeof err.detail === 'string' ? err.detail : 'Failed to extend split');
      }
    } catch (err) {
      console.error('QS host extend error:', err);
      toast.error('Connection error');
    } finally {
      setIsExtending(false);
      setExtendHours(null);
    }
  };

  const handleMarkExternalPaid = async () => {
    if (!confirmExtSlot) return;
    setIsMarkingExt(true);
    try {
      const name = (extPayerName || '').trim();
      const res = await fetch(`${BACKEND_URL}/api/quick-split/sessions/${sessionId}/mark-external`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ payer_name: name || `External Payer ${confirmExtSlot}` }),
      });
      if (res.ok) { toast.success('Payment marked'); setConfirmExtSlot(null); setExtPayerName(''); fetchSession(); }
      else { const err = await res.json(); toast.error(typeof err.detail === 'string' ? err.detail : 'Failed'); }
    } catch { toast.error('Connection error'); }
    finally { setIsMarkingExt(false); }
  };

  const [confirmingExtId, setConfirmingExtId] = useState(null);
  const handleConfirmExternalDeclaration = async (paymentId) => {
    setConfirmingExtId(paymentId);
    try {
      const res = await fetch(`${BACKEND_URL}/api/quick-split/sessions/${sessionId}/confirm-external`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ payment_id: paymentId }),
      });
      if (res.ok) { toast.success('External payment confirmed'); fetchSession(); }
      else { const err = await res.json(); toast.error(typeof err.detail === 'string' ? err.detail : 'Failed'); }
    } catch { toast.error('Connection error'); }
    finally { setConfirmingExtId(null); }
  };

  const statusBadge = (status) => {
    if (status === 'active') return <Badge className="bg-green-100 text-green-700 border-green-200" data-testid="qs-host-badge-active">Active</Badge>;
    if (status === 'completed') return <Badge className="bg-blue-100 text-blue-700 border-blue-200" data-testid="qs-host-badge-completed">Completed</Badge>;
    if (status === 'expired') return <Badge className="bg-red-100 text-red-700 border-red-200" data-testid="qs-host-badge-expired">Expired</Badge>;
    if (status === 'closed') return <Badge className="bg-gray-100 text-gray-600 border-gray-200" data-testid="qs-host-badge-closed">Closed</Badge>;
    return <Badge className="bg-gray-100 text-gray-600 border-gray-200">{status}</Badge>;
  };

  if (pageState === 'loading') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  if (pageState === 'error') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-sm w-full text-center p-8">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
          <p className="text-gray-700 font-medium">{errorMsg}</p>
          <Button onClick={() => navigate(-1)} className="mt-4" variant="outline">Go back</Button>
        </Card>
      </div>
    );
  }

  const s = session;
  // Collectible count excludes the host (host is 1 of number_of_people but pays their own share)
  const collectible = s ? Math.max(0, (s.collectible_count ?? (s.number_of_people - 1))) : 0;
  const progress = collectible > 0 ? (s.paid_count / collectible) * 100 : 0;
  const isComplete = s?.status === 'completed';
  const isClosed = s?.status === 'closed' || s?.status === 'cancelled';
  const isExpired = s?.status === 'expired';
  const isTerminal = isComplete || isClosed || isExpired;
  const linkActive = s?.link_active && !isTerminal;
  const canMarkExternal = (s?.status === 'active' || s?.status === 'expired') && s && s.paid_count < collectible;

  const collectedCents = s?.collected_amount_cents || 0;
  const pendingExtCount = payments.filter(p => p.payment_status === 'pending_external').length;
  const remainingPeople = s ? Math.max(0, collectible - s.paid_count - pendingExtCount) : 0;
  const remainingCents = s ? remainingPeople * s.per_person_amount_cents : 0;
  const fmt = (cents) => `$${(cents / 100).toFixed(2)}`;

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 px-6 h-16">
        <div className="max-w-7xl mx-auto h-full flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" size="icon" onClick={() => window.history.length > 1 ? navigate(-1) : navigate('/')} data-testid="qs-host-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <PayoLogo size="md" />
          </div>
          <div className="flex items-center space-x-3">
            <h1 className="hidden sm:block text-base font-semibold text-gray-900 truncate max-w-[200px]">{s?.description || 'Quick Split'}</h1>
            {s && statusBadge(s.status)}
          </div>
        </div>
      </header>

      <div className="max-w-md mx-auto px-4 py-8 space-y-5">
        {/* Counter Card */}
        <Card data-testid="qs-host-counter-card">
          <CardContent className="pt-6 text-center">
            <p className="text-sm text-gray-500 mb-1">Total: {s ? fmt(s.total_amount_cents) : ''}</p>
            <div className="flex items-center justify-center space-x-2 mb-3">
              <span className="text-5xl font-extrabold text-gray-900" data-testid="qs-host-paid-count">{s?.paid_count}</span>
              <span className="text-2xl text-gray-300 font-light">/</span>
              <span className="text-2xl text-gray-400 font-medium" data-testid="qs-host-collectible">{collectible}</span>
              <span className="text-sm text-gray-400 ml-1">collected</span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-2.5 mb-3">
              <div
                className={`h-2.5 rounded-full transition-all duration-500 ${isComplete ? 'bg-green-500' : isExpired ? 'bg-red-400' : isClosed ? 'bg-gray-400' : 'bg-blue-500'}`}
                style={{ width: `${progress}%` }}
                data-testid="qs-host-progress-bar"
              />
            </div>
            <p className="text-xs text-gray-400">{s ? fmt(s.per_person_amount_cents) : ''} per person</p>

            {s && s.paid_count > 0 && (
              <>
                <Separator className="my-4" />
                <div className="grid grid-cols-2 gap-3 text-left" data-testid="qs-host-financial-summary">
                  <div className="bg-green-50 rounded-lg p-3">
                    <p className="text-xs text-green-600 font-medium">Collected</p>
                    <p className="text-lg font-bold text-green-700" data-testid="qs-host-collected">{fmt(collectedCents)}</p>
                  </div>
                  <div className={`rounded-lg p-3 ${remainingPeople > 0 ? 'bg-amber-50' : 'bg-gray-50'}`}>
                    <p className={`text-xs font-medium ${remainingPeople > 0 ? 'text-amber-600' : 'text-gray-400'}`}>Remaining</p>
                    <p className={`text-lg font-bold ${remainingPeople > 0 ? 'text-amber-700' : 'text-gray-400'}`} data-testid="qs-host-remaining">{fmt(remainingCents)}</p>
                    {remainingPeople > 0 && <p className="text-xs text-amber-500">{remainingPeople} unpaid</p>}
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* QR + Share — only when link is active */}
        {linkActive && (
          <Card data-testid="qs-host-qr-card">
            <CardContent className="pt-6 flex flex-col items-center space-y-4">
              <p className="text-sm text-gray-600 font-medium">Share this with your group</p>
              <div className="p-4 bg-white rounded-xl border-2 border-gray-100" data-testid="qs-host-qr-code">
                <QRCodeSVG value={payUrl} size={200} level="M" />
              </div>
              <div className="flex space-x-3 w-full">
                <Button variant="outline" className="flex-1" onClick={handleCopyLink} data-testid="qs-host-copy-link-btn">
                  <Copy className="w-4 h-4 mr-2" /> Copy link
                </Button>
                <Button variant="outline" className="flex-1" onClick={handleShare} data-testid="qs-host-share-btn">
                  <Share2 className="w-4 h-4 mr-2" /> Share
                </Button>
              </div>
              {s?.link_expires_at && (
                <p className="text-xs text-gray-400">
                  <Clock className="w-3 h-3 inline mr-1" />
                  Link expires {new Date(s.link_expires_at).toLocaleTimeString()}
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Completed */}
        {isComplete && (
          <Card className="border-green-200 bg-green-50" data-testid="qs-host-complete-banner">
            <CardContent className="pt-6 text-center">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-2" />
              <p className="text-lg font-bold text-green-700">All Paid!</p>
              <p className="text-sm text-green-600">Everyone has paid their share</p>
            </CardContent>
          </Card>
        )}

        {/* Expired banner */}
        {isExpired && (
          <Card className="border-red-200 bg-red-50" data-testid="qs-host-expired-banner">
            <CardContent className="pt-6 pb-5 text-center space-y-4">
              <Clock className="w-12 h-12 text-red-400 mx-auto" />
              <div>
                <p className="text-lg font-bold text-red-700">Transaction Expired</p>
                <p className="text-sm text-red-500">{s?.paid_count} of {collectible} collected before expiry</p>
              </div>
              <div className="pt-2 space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                  Extend Expiration
                </p>
                <p className="text-xs text-gray-500 leading-snug">
                  Reopen this split with more time. Existing payments and participants are preserved. Unpaid participants will be notified.
                </p>
                <div className="grid grid-cols-2 gap-2 pt-1">
                  {[
                    { hours: 1,  label: '+1 hour' },
                    { hours: 4,  label: '+4 hours' },
                    { hours: 12, label: '+12 hours' },
                    { hours: 24, label: '+24 hours' },
                  ].map(({ hours, label }) => (
                    <Button
                      key={hours}
                      onClick={() => handleExtend(hours)}
                      disabled={isExtending}
                      variant="outline"
                      className="bg-white hover:bg-blue-50 border-blue-200 text-blue-700 font-semibold disabled:opacity-60"
                      data-testid={`qs-host-extend-${hours}h-btn`}
                    >
                      {isExtending && extendHours === hours ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        label
                      )}
                    </Button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Closed */}
        {isClosed && (
          <Card className="border-gray-200 bg-gray-50" data-testid="qs-host-closed-banner">
            <CardContent className="pt-6 text-center">
              <XCircle className="w-12 h-12 text-gray-400 mx-auto mb-2" />
              <p className="text-lg font-bold text-gray-600">Split Closed</p>
              <p className="text-sm text-gray-500">{s?.paid_count} of {collectible} collected before closing</p>
            </CardContent>
          </Card>
        )}

        {/* Close button for active transactions */}
        {s?.status === 'active' && (
          <div className="text-center">
            <Button
              variant="ghost"
              size="sm"
              className="text-gray-400 hover:text-red-500"
              onClick={handleClose}
              disabled={isClosing}
              data-testid="qs-host-close-btn"
            >
              <XCircle className="w-4 h-4 mr-1" /> Close Split
            </Button>
          </div>
        )}

        {/* Payments + Unpaid Slots */}
        <Card data-testid="qs-host-payer-list-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold flex items-center">
              <Users className="w-4 h-4 mr-2 text-gray-400" />
              Payments ({payments.length}{remainingPeople > 0 && canMarkExternal ? ` + ${remainingPeople} unpaid` : ''})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {payments.filter(p => p.payment_status !== 'failed_ach').map((p) => {
                const isExt = p.payment_method === 'external' && p.payment_status === 'completed';
                const isPendingExt = p.payment_status === 'pending_external';
                const isPendingAch = p.payment_status === 'pending_ach';
                const isAch = p.payment_method === 'ach' && !isPendingAch;

                let iconBg = 'bg-green-100';
                let IconComp = UserCheck;
                let iconColor = 'text-green-600';
                let label = 'Paid · depositing to your bank';
                let amountColor = 'text-green-600';

                if (isPendingExt) {
                  iconBg = 'bg-orange-100'; IconComp = Clock; iconColor = 'text-orange-500';
                  label = 'External · pending confirmation'; amountColor = 'text-orange-500';
                } else if (isPendingAch) {
                  iconBg = 'bg-blue-100'; IconComp = Building2; iconColor = 'text-blue-500';
                  label = 'Bank transfer pending'; amountColor = 'text-blue-500';
                } else if (isAch) {
                  iconBg = 'bg-green-100'; IconComp = Building2; iconColor = 'text-green-600';
                  label = 'Paid via ACH';
                } else if (isExt) {
                  iconBg = 'bg-orange-100'; IconComp = Banknote; iconColor = 'text-orange-600';
                  label = 'Paid (external)'; amountColor = 'text-orange-600';
                }

                return (
                  <div key={p.internal_payment_id} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0" data-testid={`qs-host-payment-${p.internal_payment_id}`}>
                    <div className="flex items-center space-x-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${iconBg}`}>
                        <IconComp className={`w-4 h-4 ${iconColor}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-1.5">
                          <p className="text-sm font-medium text-gray-900">{p.payer_name}</p>
                          {isExt && <Badge className="bg-orange-50 text-orange-600 border-orange-200 text-[10px] px-1.5 py-0 h-4" data-testid={`qs-host-ext-tag-${p.internal_payment_id}`}>External</Badge>}
                        </div>
                        <p className="text-xs text-gray-400">{label}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {isPendingExt && (
                        <Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-white text-xs h-7 px-2"
                          onClick={() => handleConfirmExternalDeclaration(p.internal_payment_id)}
                          disabled={confirmingExtId === p.internal_payment_id}
                          data-testid={`qs-host-confirm-ext-${p.internal_payment_id}`}>
                          {confirmingExtId === p.internal_payment_id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Confirm'}
                        </Button>
                      )}
                      <span className={`text-sm font-semibold ${amountColor}`}>{fmt(p.amount_cents)}</span>
                    </div>
                  </div>
                );
              })}

              {canMarkExternal && Array.from({ length: remainingPeople }, (_, i) => i + 1).map((n) => (
                <div key={`unpaid-${n}`} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0" data-testid={`qs-host-unpaid-slot-${n}`}>
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center"><Users className="w-4 h-4 text-gray-400" /></div>
                    <div>
                      <p className="text-sm text-gray-400">Unpaid slot #{n}</p>
                      <p className="text-xs text-gray-300">{fmt(s?.per_person_amount_cents || 0)}</p>
                    </div>
                  </div>
                  <Button size="sm" variant="outline" className="text-orange-600 border-orange-200 hover:bg-orange-50" onClick={() => { setExtPayerName(''); setConfirmExtSlot(n); }} data-testid={`qs-host-mark-ext-btn-${n}`}>
                    <Banknote className="w-3 h-3 mr-1" /> Mark as paid (external)
                  </Button>
                </div>
              ))}

              {payments.length === 0 && !canMarkExternal && (
                <p className="text-sm text-gray-400 text-center py-4" data-testid="qs-host-no-payments">No payments</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {confirmExtSlot !== null && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="qs-ext-confirm-modal">
          <Card className="max-w-sm w-full">
            <CardContent className="pt-6 space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 text-center">Mark as paid (external)</h3>
              <p className="text-sm text-gray-600 text-center">
                Record that you received <span className="font-bold">{fmt(s?.per_person_amount_cents || 0)}</span> as paid externally. No Stripe charge, no fees.
              </p>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-gray-500">Who paid? <span className="text-gray-400">(optional)</span></label>
                <input
                  type="text"
                  value={extPayerName}
                  onChange={(e) => setExtPayerName(e.target.value)}
                  maxLength={50}
                  placeholder="e.g. Alex"
                  className="w-full h-10 px-3 rounded-md border border-gray-200 text-sm focus:outline-none focus:border-orange-400"
                  data-testid="qs-ext-payer-name-input"
                />
              </div>
              <div className="flex space-x-3 pt-1">
                <Button variant="outline" className="flex-1" onClick={() => { setConfirmExtSlot(null); setExtPayerName(''); }} data-testid="qs-ext-cancel-btn">Cancel</Button>
                <Button className="flex-1 bg-orange-500 hover:bg-orange-600 text-white" onClick={handleMarkExternalPaid} disabled={isMarkingExt} data-testid="qs-ext-confirm-btn">
                  {isMarkingExt ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}Mark as paid
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default QuickSplitHost;
